/*
 * Product reviews — the bits Shopify's comment form cannot do on its own.
 *
 * A blog comment accepts exactly three fields: author, email and body. There is
 * nowhere to put a star rating, so the rating radios are not submitted at all —
 * on submit their value is folded into the front of the body as a "[4]" token,
 * which sections/product-reviews.liquid parses back off before display.
 *
 * That token is the whole contract between the two files. If you change it
 * here, change the parser there too.
 *
 * Clicks and submits are handled by delegated listeners on the document rather
 * than bound per element. Section markup arrives at several different moments —
 * initial render, theme-editor reload, the refresh after a post — and binding
 * per element meant anything appearing late silently had no handler at all.
 */
(function () {
  const RATING_TOKEN = /^\s*\[[1-5]\]\s*/;

  /* ---------------------------------------------------------------- clicks */

  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-reviews-more]');
    if (more) return revealMore(more);

    const open = event.target.closest('[data-reviews-open]');
    if (open) return openForm(open);

    // The rating at the top of the page jumps here. Handled rather than left to
    // the browser so it eases instead of snapping, and so a sticky header does
    // not land on top of the heading.
    const jump = event.target.closest('[data-reviews-jump]');
    if (jump) {
      const target = document.querySelector('#reviews');
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#reviews');
      }
      return;
    }

    const demoSubmit = event.target.closest('[data-reviews-demo-submit]');
    if (demoSubmit) {
      const note = demoSubmit.parentElement?.querySelector('[data-reviews-demo-note]');
      if (note) note.hidden = false;
    }
  });

  /*
   * Reveal rows already in the document rather than fetching. The list renders
   * deep and everything past the first batch starts [hidden], so a click is
   * instant and costs no request.
   */
  function revealMore(button) {
    const root = button.closest('.reviews') || document;
    const step = parseInt(button.dataset.reviewsStep, 10) || 6;

    const hidden = Array.from(root.querySelectorAll('.reviews__item[hidden]'));
    hidden.slice(0, step).forEach((item) => item.removeAttribute('hidden'));

    const counter = root.querySelector('[data-reviews-shown]');
    if (counter) counter.textContent = root.querySelectorAll('.reviews__item:not([hidden])').length;

    if (hidden.length <= step) button.hidden = true;
  }

  function openForm(button) {
    const root = button.closest('.reviews') || document;
    const panel = root.querySelector('[data-reviews-form]');
    if (!panel) return;

    panel.removeAttribute('hidden');
    panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    panel.querySelector('input, textarea')?.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------ submitting */

  document.addEventListener(
    'submit',
    (event) => {
      const panel = event.target.closest('[data-reviews-form]');
      if (!panel) return;

      const form = event.target;
      const body = panel.querySelector('[data-reviews-body]');
      const group = panel.querySelector('[data-reviews-rating]');
      if (!body) return;

      const checked = group && group.querySelector('input:checked');
      // Never stack tokens: a failed post round-trips the body back into the
      // textarea with the last attempt's token already on the front.
      const text = body.value.replace(RATING_TOKEN, '');
      body.value = checked ? `[${checked.value}] ${text}` : text;

      // Shopify's comment form posts to the blog article and redirects there.
      // Send it ourselves so the shopper stays on the product page. Where fetch
      // is missing this falls through to the normal submit, which still posts —
      // it just navigates, which beats a button that does nothing.
      if (typeof fetch !== 'function') return;
      event.preventDefault();
      post(panel.closest('.reviews') || panel, form);
    },
    true
  );

  async function post(section, form) {
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      // Shopify answers validation failures by re-rendering the article with a
      // status list, not with an error code, so the body is the only signal.
      const failed = !response.ok || doc.querySelector('.form-status-list, .form__message[role="alert"]');

      message(section, failed ? 'error' : 'ok');

      if (!failed) {
        form.reset();
        // An unmoderated blog publishes straight away, so pull the section back
        // down and the new review is simply there.
        if (section.dataset && section.dataset.reviewsModerated === 'false') refresh(section);
      }
    } catch (error) {
      message(section, 'error');
    } finally {
      if (submit) submit.removeAttribute('disabled');
    }
  }

  /* Re-render this section from the server and swap the list back in. */
  async function refresh(section) {
    const id = section.dataset.reviewsSection;
    if (!id) return;

    try {
      const response = await fetch(`${window.location.pathname}?section_id=${id}`);
      if (!response.ok) return;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-reviews-list]');
      const current = section.querySelector('[data-reviews-list]');
      if (!fresh || !current) return;

      // The server always sends the first batch visible and the rest hidden, so
      // anything already revealed with "Show more" has to be re-revealed or the
      // list would visibly collapse under the reader.
      const wasVisible = current.querySelectorAll('.reviews__item:not([hidden])').length;
      current.innerHTML = fresh.innerHTML;

      Array.from(current.querySelectorAll('.reviews__item'))
        .slice(0, Math.max(wasVisible, 1))
        .forEach((item) => item.removeAttribute('hidden'));

      const counter = section.querySelector('[data-reviews-shown]');
      if (counter) counter.textContent = current.querySelectorAll('.reviews__item:not([hidden])').length;

      const more = section.querySelector('[data-reviews-more]');
      if (more) more.hidden = current.querySelectorAll('.reviews__item[hidden]').length === 0;
    } catch (error) {
      /* The review posted either way; a stale list is not worth an alarm. */
    }
  }

  function message(section, kind) {
    const panel = section.querySelector ? section.querySelector('[data-reviews-form]') : null;
    if (!panel) return;

    let note = panel.querySelector('[data-reviews-status]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-reviews-status', '');
      panel.prepend(note);
    }

    const moderated = section.dataset && section.dataset.reviewsModerated === 'true';
    note.className = `reviews__message reviews__message--${kind === 'ok' ? 'ok' : 'error'}`;
    note.textContent =
      kind === 'ok'
        ? moderated
          ? 'Thanks — your review will appear once it has been approved.'
          : 'Thanks — your review is live.'
        : 'Something went wrong. Please check the fields and try again.';
    note.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
})();
